'use client';

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  getConsent,
  onConsentChange,
  setConsent,
  type ConsentState,
} from '@/lib/marketing/consent';

type ConsentBannerProps = {
  className?: string;
};

const ENTER_DELAY_MS = 140;
const EXIT_DELAY_MS = 260;

type TimeoutRef = { current: ReturnType<typeof setTimeout> | null };

const clearTimeoutRef = (ref: TimeoutRef) => {
  if (ref.current) {
    globalThis.clearTimeout(ref.current);
    ref.current = null;
  }
};

const clearAllTimers = (enterRef: TimeoutRef, exitRef: TimeoutRef) => {
  clearTimeoutRef(enterRef);
  clearTimeoutRef(exitRef);
};

const scheduleHide = (
  exitRef: TimeoutRef,
  setShouldRender: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  clearTimeoutRef(exitRef);
  exitRef.current = globalThis.setTimeout(() => {
    setShouldRender(false);
    exitRef.current = null;
  }, EXIT_DELAY_MS);
};

const scheduleReveal = (
  shouldRender: boolean,
  setShouldRender: React.Dispatch<React.SetStateAction<boolean>>,
  enterRef: TimeoutRef,
  setVisible: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  if (shouldRender) {
    if (enterRef.current) return;
  } else {
    setShouldRender(true);
  }

  clearTimeoutRef(enterRef);

  enterRef.current = globalThis.setTimeout(() => {
    setVisible(true);
    enterRef.current = null;
  }, ENTER_DELAY_MS);
};

const scheduleReopen = (
  enterRef: TimeoutRef,
  setVisible: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  requestAnimationFrame(() => {
    enterRef.current = globalThis.setTimeout(() => {
      setVisible(true);
      enterRef.current = null;
    }, 60);
  });
};

export function ConsentBanner({ className = '' }: Readonly<ConsentBannerProps>) {
  const consent = useSyncExternalStore(
    (listener) => onConsentChange(() => listener()),
    () => getConsent(),
    () => null,
  );
  const [pending, setPending] = useState(() => consent === null);

  const [shouldRender, setShouldRender] = useState(false);
  const [visible, setVisible] = useState(false);
  const [working, setWorking] = useState(false);
  const enterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const finalize = (nextState: ConsentState) => {
    setConsent(nextState);
    setWorking(false);
    setVisible(false);
    setPending(false);
    scheduleHide(exitTimer, setShouldRender);
  };

  const acceptAll = () => {
    setWorking(true);
    finalize({ analytics: true, ads: true });
  };

  const rejectAll = () => {
    setWorking(true);
    finalize({ analytics: false, ads: false });
  };

  useEffect(() => {
    if (!pending) {
      return;
    }
    scheduleReveal(shouldRender, setShouldRender, enterTimer, setVisible);

    return () => undefined;
  }, [pending, shouldRender]);

  useEffect(() => {
    const offConsent = onConsentChange(() => {
      setPending(false);
      setVisible(false);
      setWorking(false);
      scheduleHide(exitTimer, setShouldRender);
    });

    const handleReopen = () => {
      clearAllTimers(enterTimer, exitTimer);
      setShouldRender(true);
      scheduleReopen(enterTimer, setVisible);
    };

    globalThis.addEventListener('openConsentManager', handleReopen);

    return () => {
      clearAllTimers(enterTimer, exitTimer);
      offConsent();
      globalThis.removeEventListener('openConsentManager', handleReopen);
    };
  }, []);

  useEffect(() => {
    return () => {
      clearAllTimers(enterTimer, exitTimer);
    };
  }, []);

  if (!shouldRender) return null;

  const containerStateClasses = visible
    ? 'translate-y-0 opacity-100 pointer-events-auto'
    : 'translate-y-4 opacity-0 pointer-events-none';

  return (
    <dialog
      open
      aria-live="polite"
      aria-label="Consentimento de cookies"
      className={[
        'fixed inset-x-0 bottom-0 z-[60] px-fluid-x pb-6 bg-transparent',
        className,
      ].join(' ')}
    >
      <div
        className={[
          'mx-auto max-w-container-max-wide rounded-2xl border border-[color:color-mix(in_srgb,var(--color-ink)_18%,transparent)] bg-white shadow-[0_16px_60px_rgba(0,0,0,0.10)]',
          'transition-all duration-400 ease-[cubic-bezier(0.16,1,0.3,1)]',
          containerStateClasses,
        ].join(' ')}
      >
        <div className="grid gap-5 p-5 md:grid-cols-[1fr_auto] md:items-center md:p-6">
          <div className="text-sm leading-6 text-ink">
            <p className="text-ink">
              Este website utiliza dados de utilização e medição para análises,
              desempenho e marketing.
            </p>
            <p className="mt-2 text-ink-64">
              Ao continuar a utilizar o site, reconhece este processamento.
              Consulte a nossa Política de Privacidade para mais detalhes.
            </p>
          </div>
          <div className="flex items-center gap-3 md:justify-end">
            <button
              type="button"
              onClick={rejectAll}
              disabled={working}
              className="h-11 rounded-xl border border-[color:color-mix(in_srgb,var(--color-ink)_18%,transparent)] bg-white px-5 text-sm font-medium text-ink transition-colors hover:border-ink/50 disabled:opacity-50"
            >
              Rejeitar
            </button>
            <button
              type="button"
              onClick={acceptAll}
              disabled={working}
              className="h-11 rounded-xl bg-black px-6 text-sm font-semibold text-white shadow-[inset_0_-2px_0_rgba(0,0,0,0.08)] transition-transform hover:translate-y-[-1px] disabled:opacity-50"
              aria-label="Aceitar todos os cookies de análise"
            >
              Aceitar
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
}

export default ConsentBanner;
